import React, { useEffect, useReducer, useState } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { ArrowBack, DeleteOutline, Edit, Refresh } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  formCard: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 2fr",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  formActions: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_HOLIDAYS") {
    return action.payload;
  }

  if (action.type === "UPSERT_HOLIDAY") {
    const holiday = action.payload;
    const holidayIndex = state.findIndex((item) => item.id === holiday.id);

    if (holidayIndex !== -1) {
      const nextState = [...state];
      nextState[holidayIndex] = holiday;
      return nextState.sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));
    }

    return [...state, holiday].sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));
  }

  if (action.type === "DELETE_HOLIDAY") {
    return state.filter((item) => item.id !== action.payload);
  }

  return state;
};

const emptyForm = {
  id: null,
  name: "",
  holidayDate: "",
  message: "",
};

const CompanyHolidays = () => {
  const classes = useStyles();
  const history = useHistory();
  const [holidays, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const loadHolidays = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/company-holidays");
        dispatch({
          type: "LOAD_HOLIDAYS",
          payload: Array.isArray(data) ? data : [],
        });
      } catch (error) {
        toastError(error);
      } finally {
        setLoading(false);
      }
    };

    loadHolidays();
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company-holidays");
      dispatch({
        type: "LOAD_HOLIDAYS",
        payload: Array.isArray(data) ? data : [],
      });
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.holidayDate) {
      toast.error("Preencha o nome e a data do feriado.");
      return;
    }

    try {
      if (form.id) {
        const { data } = await api.put(`/company-holidays/${form.id}`, {
          name: form.name,
          holidayDate: form.holidayDate,
          message: form.message,
        });
        dispatch({ type: "UPSERT_HOLIDAY", payload: data });
        toast.success("Feriado atualizado.");
      } else {
        const { data } = await api.post("/company-holidays", {
          name: form.name,
          holidayDate: form.holidayDate,
          message: form.message,
        });
        dispatch({ type: "UPSERT_HOLIDAY", payload: data });
        toast.success("Feriado cadastrado.");
      }

      setForm(emptyForm);
    } catch (error) {
      toastError(error);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      await api.delete(`/company-holidays/${deleteTarget.id}`);
      dispatch({ type: "DELETE_HOLIDAY", payload: deleteTarget.id });
      toast.success("Feriado removido.");
      setDeleteTarget(null);
      if (form.id === deleteTarget.id) {
        setForm(emptyForm);
      }
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleteTarget ? `Remover ${deleteTarget.name}?` : "Remover feriado?"}
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      >
        Essa exclusão não poderá ser desfeita.
      </ConfirmationModal>

      <MainHeader>
        <Title>Feriados</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={() => history.push("/company")}
          >
            Voltar para Empresa
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Atualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Paper className={classes.formCard} variant="outlined">
          <div>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Cadastro de feriados
            </Typography>
            <Typography variant="body2" color="textSecondary">
              A mensagem do feriado tem prioridade sobre a mensagem geral de ausência.
            </Typography>
          </div>

          <div className={classes.formRow}>
            <TextField
              fullWidth
              variant="outlined"
              label="Nome do feriado"
              value={form.name}
              onChange={(event) => {
                const { value } = event.target;
                setForm((current) => ({ ...current, name: value }));
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Data"
              type="date"
              value={form.holidayDate}
              onChange={(event) => {
                const { value } = event.target;
                setForm((current) => ({
                  ...current,
                  holidayDate: value,
                }));
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Mensagem de ausência no feriado"
              value={form.message}
              multiline
              rows={2}
              onChange={(event) => {
                const { value } = event.target;
                setForm((current) => ({ ...current, message: value }));
              }}
              helperText="Se ficar em branco, o sistema usa a mensagem geral de ausência."
            />
          </div>

          <div className={classes.formActions}>
            <Button variant="outlined" onClick={() => setForm(emptyForm)}>
              Limpar
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              {form.id ? "Salvar feriado" : "Cadastrar feriado"}
            </Button>
          </div>
        </Paper>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Feriado</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Mensagem</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {holidays.length > 0 ? (
              holidays.map((holiday) => (
                <TableRow key={holiday.id}>
                  <TableCell>{holiday.name}</TableCell>
                  <TableCell>
                    {new Date(`${holiday.holidayDate}T00:00:00`).toLocaleDateString(
                      "pt-BR"
                    )}
                  </TableCell>
                  <TableCell>{holiday.message || "Usando mensagem geral"}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setForm({
                          id: holiday.id,
                          name: holiday.name,
                          holidayDate: holiday.holidayDate,
                          message: holiday.message || "",
                        })
                      }
                    >
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeleteTarget(holiday)}>
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhum feriado cadastrado ainda.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default CompanyHolidays;
